/**
 * Turning reminders on, from the browser's side.
 *
 * Three platform facts to work around, none of them optional:
 *
 * iOS grants the Push API only to a web app that has been added to the home
 * screen. In a Safari tab `Notification` may not exist at all, and where it
 * does, `requestPermission` resolves to "denied" without ever asking. So the
 * unsupported case has to be a real state with a real explanation, not a button
 * that quietly does nothing.
 *
 * Permission is per origin and permanent once refused. There is no second
 * prompt and no API to re-ask; the only route back is browser settings. Asking
 * at the wrong moment therefore burns the only chance, which is why the switch
 * is the thing that asks rather than page load.
 *
 * And the application server key has to be raw bytes. The server sends the
 * base64url text it was generated with, so it is decoded here.
 */

import { api } from '../api/client.js'

export type PushSupport = 'ready' | 'needs-install' | 'unsupported'

/** Standalone-only on iOS, which is the whole reason this has three answers. */
export function pushSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    const iosLike =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const standalone =
      (navigator as unknown as { standalone?: boolean }).standalone === true ||
      window.matchMedia?.('(display-mode: standalone)').matches === true
    return iosLike && !standalone ? 'needs-install' : 'unsupported'
  }
  return 'Notification' in window ? 'ready' : 'unsupported'
}

export function permissionState(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

/**
 * The key has to reach `subscribe` as raw bytes; the server sends the base64url
 * text it was generated with. Backed by a plain ArrayBuffer rather than
 * whatever `Uint8Array` happens to sit on, because `BufferSource` will not take
 * a view over a SharedArrayBuffer.
 */
function urlBase64ToBytes(base64: string): ArrayBuffer {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const buffer = new ArrayBuffer(raw.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return buffer
}

export interface ReminderPreferences {
  eveningEnabled: boolean
  weeklyEnabled: boolean
  weighInEnabled: boolean
  eveningHour: number
  morningHour: number
  morningBriefEnabled: boolean
  briefHour: number
}

export const DEFAULT_PREFERENCES: ReminderPreferences = {
  eveningEnabled: true,
  weeklyEnabled: true,
  weighInEnabled: true,
  eveningHour: 20,
  morningHour: 8,
  morningBriefEnabled: false,
  briefHour: 9,
}

export interface SubscriptionState {
  support: PushSupport
  permission: NotificationPermission | 'unsupported'
  /** The endpoint this browser is registered under, or null if it is not. */
  endpoint: string | null
  /** False when the server has no VAPID keys, so the switch can say why. */
  serverReady: boolean
  preferences: ReminderPreferences
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

export async function readSubscriptionState(): Promise<SubscriptionState> {
  const support = pushSupport()
  const base: SubscriptionState = {
    support,
    permission: permissionState(),
    endpoint: null,
    serverReady: false,
    preferences: DEFAULT_PREFERENCES,
  }
  if (support !== 'ready') return base

  const [existing, key] = await Promise.all([
    currentSubscription().catch(() => null),
    api.get<{ key: string; enabled: boolean }>('/api/push/key').catch(() => null),
  ])
  base.serverReady = key?.enabled === true
  base.endpoint = existing?.endpoint ?? null

  if (existing) {
    const stored = await api
      .get<{ subscriptions: Array<ReminderPreferences & { endpoint: string }> }>(
        '/api/push/subscriptions',
      )
      .catch(() => null)
    const mine = stored?.subscriptions.find((s) => s.endpoint === existing.endpoint)
    if (mine) {
      base.preferences = {
        eveningEnabled: mine.eveningEnabled,
        weeklyEnabled: mine.weeklyEnabled,
        weighInEnabled: mine.weighInEnabled,
        eveningHour: mine.eveningHour,
        morningHour: mine.morningHour,
        morningBriefEnabled: mine.morningBriefEnabled,
        briefHour: mine.briefHour,
      }
    }
  }
  return base
}

export type EnableResult = 'enabled' | 'denied' | 'unsupported' | 'server-unconfigured' | 'failed'

/**
 * Ask for permission and register with the push service.
 *
 * The order matters: the server's key is fetched first, so a server with no
 * VAPID pair never spends the user's one-and-only permission prompt on a
 * subscription it cannot then create.
 */
export async function enableReminders(
  preferences: ReminderPreferences = DEFAULT_PREFERENCES,
): Promise<EnableResult> {
  if (pushSupport() !== 'ready') return 'unsupported'

  const key = await api.get<{ key: string; enabled: boolean }>('/api/push/key').catch(() => null)
  if (!key?.enabled || key.key === '') return 'server-unconfigured'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'denied'

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        // Non-negotiable on every browser that ships push: a subscription that
        // may deliver a silent message is refused outright.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBytes(key.key),
      }))

    const json = subscription.toJSON()
    await api.post('/api/push/subscribe', {
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...preferences,
    })
    return 'enabled'
  } catch {
    return 'failed'
  }
}

export async function disableReminders(): Promise<void> {
  const subscription = await currentSubscription().catch(() => null)
  if (!subscription) return
  await api.post('/api/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {})
  await subscription.unsubscribe().catch(() => {})
}

export async function updatePreferences(changes: Partial<ReminderPreferences>): Promise<void> {
  const subscription = await currentSubscription().catch(() => null)
  if (!subscription) return
  await api.patch('/api/push/preferences', { endpoint: subscription.endpoint, ...changes })
}

/** Send one now, because "no reminder arrived" is not evidence of anything. */
export async function sendTestReminder(): Promise<boolean> {
  const subscription = await currentSubscription().catch(() => null)
  if (!subscription) return false
  const res = await api
    .post<{ result: string }>('/api/push/test', { endpoint: subscription.endpoint })
    .catch(() => null)
  return res?.result === 'sent'
}
